export type AdminErrorDisplay = {
  type: 'toast' | 'dialog';
  message: string;
};

const ADMIN_ERROR_COPY: Record<number, AdminErrorDisplay> = {
  46001: {
    type: 'toast',
    message: '当前账号没有该操作权限',
  },
  46002: {
    type: 'toast',
    message: '当前记录状态不支持此审核动作',
  },
  46003: {
    type: 'toast',
    message: '当前状态流转不合法，请刷新后重试',
  },
  46004: {
    type: 'toast',
    message: '配置保存失败，请稍后再试',
  },
  46005: {
    type: 'dialog',
    message: '目标记录不存在或已被处理',
  },
};

type CsvColumn = {
  title: string;
  dataIndex: string;
};

type SorterValue = 'ascend' | 'descend' | null | undefined;

const ADMIN_ENUM_LABELS: Record<string, string> = {
  active: '启用',
  adjusted: '已调整',
  admin: '管理员',
  admin_create: '后台创建',
  approved: '已通过',
  arrived: '已到店',
  assigned: '后台分配',
  available: '可提现',
  blocked: '已拦截',
  bound: '已绑定',
  booked: '已预约',
  broker: '红娘',
  broker_direct: '红娘本人发展',
  broker_invite: '红娘发展',
  broker_register: '红娘入驻',
  cancelled: '已取消',
  chat_violation: '聊天违规',
  closed: '已关闭',
  completed: '已完成',
  confirmed: '已确认',
  contact: '联系方式认证',
  custom_intent: '私人定制意向金',
  deleted: '已删除',
  disabled: '停用',
  draft: '草稿',
  enabled: '启用中',
  failed: '失败',
  fake_identity: '冒充身份',
  fake_profile: '资料造假',
  female: '女',
  flagged: '风险预警',
  frozen: '冻结',
  gallery: '相册照',
  identity: '实名认证',
  inactive: '停用',
  invite_fee: '邀请费用',
  leader: '团长',
  leader_assigned: '已分派主责团长',
  leader_direct: '团长亲自发展',
  leader_register: '团长入驻',
  male: '男',
  member: '会员',
  member_invite: '会员邀请',
  member_register: '通讯费用',
  miniapp_apply: '小程序申请',
  normal: '正常',
  not_required: '无需审核',
  organic_share: '自然分享',
  paid: '已支付',
  passed: '已通过',
  pending: '待处理',
  pending_contact: '待接待',
  pending_dispatch: '待后台分派',
  pending_leader: '待团长确认',
  pending_payment: '待支付',
  pending_reply: '待回复',
  pending_review: '待审核',
  promotion_fee: '推广费用',
  promotion_reward: '推广奖励',
  refunded: '已退款',
  rejected: '已驳回',
  settled: '已结算',
  skipped: '已跳过',
  store: '门店',
  store_natural: '门店自然到店',
  submitted: '申请提交',
  system: '系统',
  unbound: '未绑定',
};

const BUSINESS_TYPE_LABELS: Record<string, string> = {
  broker_register: '红娘入驻',
  custom_intent: '私人定制意向金',
  leader_register: '团长入驻',
  member_register: '通讯费用',
  promotion_reward: '推广奖励',
};

const ROLE_TYPE_LABELS: Record<string, string> = {
  admin: '管理员',
  broker: '红娘',
  leader: '团长',
  merchant: '门店店长',
  member: '会员',
  store: '门店',
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  cancelled: '已取消',
  failed: '支付失败',
  paid: '已支付',
  pending: '待支付',
  refunded: '已退款',
};

const LEDGER_STATUS_LABELS: Record<string, string> = {
  available: '可提现',
  paid: '已打款',
  pending: '待结算',
  settled: '已结算',
};

const WITHDRAWAL_STATUS_LABELS: Record<string, string> = {
  approved: '已通过',
  paid: '已打款',
  pending_review: '待审核',
  rejected: '已驳回',
};

function formatMappedLabel(
  value: unknown,
  map: Record<string, string>,
  fallbackLabel: string,
) {
  const normalizedValue = normalizeValue(value).trim();
  if (!normalizedValue) {
    return '未设置';
  }

  return map[normalizedValue] ?? ADMIN_ENUM_LABELS[normalizedValue] ?? fallbackLabel;
}

function normalizeValue(value: unknown) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (Array.isArray(value)) {
    return value.join(' / ');
  }

  return String(value);
}

function escapeCsvValue(value: unknown) {
  const text = normalizeValue(value);
  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
}

export function getAdminErrorDisplay(
  code?: number,
  fallbackMessage = '请求失败，请稍后重试',
): AdminErrorDisplay {
  return (
    (code ? ADMIN_ERROR_COPY[code] : undefined) ?? {
      type: 'toast',
      message: fallbackMessage,
    }
  );
}

export function filterByKeyword<T extends Record<string, unknown>>(
  records: T[] = [],
  keyword = '',
  fields: (keyof T)[] = [],
) {
  const normalizedKeyword = keyword.trim().toLowerCase();
  if (!normalizedKeyword) {
    return records;
  }

  return records.filter((record) =>
    fields.some((field) =>
      normalizeValue(record[field]).toLowerCase().includes(normalizedKeyword),
    ),
  );
}

export function countByField<T extends Record<string, unknown>>(
  records: T[] = [],
  field: keyof T,
) {
  return records.reduce<Record<string, number>>((result, record) => {
    const key = normalizeValue(record[field]) || 'unknown';
    result[key] = (result[key] ?? 0) + 1;
    return result;
  }, {});
}

export function formatAdminEnumLabel(value?: unknown, fallbackLabel = '未知') {
  return formatMappedLabel(value, ADMIN_ENUM_LABELS, fallbackLabel);
}

export function formatBusinessTypeLabel(value?: unknown) {
  return formatMappedLabel(value, BUSINESS_TYPE_LABELS, '未知业务类型');
}

export function formatRoleTypeLabel(value?: unknown) {
  return formatMappedLabel(value, ROLE_TYPE_LABELS, '未知角色');
}

export function formatPaymentStatusLabel(value?: unknown) {
  return formatMappedLabel(value, PAYMENT_STATUS_LABELS, '未知支付状态');
}

export function formatLedgerStatusLabel(value?: unknown) {
  return formatMappedLabel(value, LEDGER_STATUS_LABELS, '未知台账状态');
}

export function formatWithdrawalStatusLabel(value?: unknown) {
  return formatMappedLabel(value, WITHDRAWAL_STATUS_LABELS, '未知提现状态');
}

export function formatApplicationSourceLabel(value?: unknown) {
  return formatMappedLabel(value, ADMIN_ENUM_LABELS, '未知申请来源');
}

export function formatAuditStatusLabel(status?: string | null) {
  const normalizedStatus = status?.trim();
  if (!normalizedStatus) {
    return '--';
  }

  if (normalizedStatus === 'pending' || normalizedStatus === 'pending_review') {
    return '待审核';
  }

  if (normalizedStatus === 'approved' || normalizedStatus === 'passed') {
    return '已通过';
  }

  if (normalizedStatus === 'rejected') {
    return '已驳回';
  }

  return formatAdminEnumLabel(normalizedStatus, '未知审核状态');
}

export function isPendingAuditStatus(status?: string | null) {
  const normalizedStatus = status?.trim();
  return normalizedStatus === 'pending' || normalizedStatus === 'pending_review';
}

export function formatBankCardDisplay(
  fullBankCardNo?: string | null,
  maskedBankCardNo?: string | null,
) {
  return fullBankCardNo || maskedBankCardNo || '--';
}

export function buildCsvContent(columns: CsvColumn[], rows: Record<string, unknown>[]) {
  const header = columns.map((column) => escapeCsvValue(column.title)).join(',');
  const lines = rows.map((row) =>
    columns.map((column) => escapeCsvValue(row[column.dataIndex])).join(','),
  );

  return [header, ...lines].join('\r\n');
}

export function downloadCsv(
  filename: string,
  columns: CsvColumn[],
  rows: Record<string, unknown>[],
) {
  const blob = new Blob([`\uFEFF${buildCsvContent(columns, rows)}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function buildSortQuery(
  sorter: Record<string, SorterValue> = {},
) {
  const matched = Object.entries(sorter).find(
    ([, order]) => order === 'ascend' || order === 'descend',
  );

  if (!matched) {
    return {};
  }

  const [sortField, sortOrder] = matched;
  return {
    sort_field: sortField,
    sort_order: sortOrder,
  };
}

export function buildExportQuery(
  params: Record<string, unknown> = {},
  sortQuery: Record<string, unknown> = {},
  pageSize = 10000,
) {
  const query: Record<string, unknown> = {
    ...params,
    ...sortQuery,
    page: 1,
    page_size: pageSize,
  };

  delete query.current;
  delete query.pageSize;

  Object.keys(query).forEach((key) => {
    const value = query[key];
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      delete query[key];
    }
  });

  return query;
}

export function buildTaskQuery(
  params: Record<string, unknown> = {},
  sortQuery: Record<string, unknown> = {},
) {
  const query: Record<string, unknown> = {
    ...params,
    ...sortQuery,
  };

  delete query.current;
  delete query.pageSize;
  delete query.page;
  delete query.page_size;

  Object.keys(query).forEach((key) => {
    const value = query[key];
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      delete query[key];
    }
  });

  return query;
}
